import * as React from "react";
import {VeBoxCard} from "../../components/card/VeBoxCard";
import {VeboxModels} from "../../types/veboxModels";
import {useUserDetails} from "../contexts/UserDetailsContext";
import {Loader} from "../../components/Utils/loader/Loader";
import {Display} from "../../components/Display";
import {InputAddon, TextInput} from "@vebox/etui-core";
import {VeboxTreeView} from "../../components/tree_view/VeboxTreeView";
import {commercialHierarchyGUID} from "../../processes/user_creation/stages/Visibility";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {useCommonData} from "../contexts/CommonDataProvider";
import {DATA_STATE} from "../contexts/UsersDashboardContext";
import {HierarchyError, useUserHierarchyVisibility} from "../contexts/UserVisibilityHierarchiesContext";
import {
    CommercialHierarchyNode,
    CommercialHierarchyTreeToList,
    EditableHierarchy,
    expandCommercialHierarchy,
    getAllSelectedNodes,
    HierarchiesSection,
    hierarchyIncludesPlacedNode,
    hierarchyIncludesPlacedNodes,
    hierarchyIncludesWord,
    HierarchyTreeToList,
    IconInfoLegend,
    isPlacedHereInCommercialHierarchy,
    VisibilityIcon
} from "../HierarchyVisibilityUtils";
import {VISIBILITY_TREE_ICONS} from "../UserManagement";
import {SearchIcon} from "../components/SearchIcon";
import {RestDataStatus} from "../../hooks/useRestData";
import {ErrorType} from "../UserDetails";

declare var AppTranslations: AppTranslations;

type UserVisibilityProps = {
    setError: (errorType: ErrorType) => void
    readOnly: boolean
    showEditVisibilitySection: () => void
}


export const UserHierarchyVisibility: React.FC<UserVisibilityProps> = ({
                                                                           setError,
                                                                           readOnly,
                                                                           showEditVisibilitySection
                                                                       }) => {

    const [currentUser] = useUserDetails();
    const [currentUserHierarchies, currentUserHierarchiesState] = useUserHierarchyVisibility();
    const {hierarchiesTree, hierarchiesTreeStatus} = useCommonData();

    const [state, setState] = React.useState<DATA_STATE>(DATA_STATE.isLoading)
    const [hasFullVisibility, setHasFullVisibility] = React.useState<boolean>();
    const [isInCeasedCompany, setIsInCeasedCompany] = React.useState<boolean>();
    const [hierarchiesSelected, setHierarchiesSelected] = React.useState<HierarchiesSection>();
    const [searchWord, setSearchWord] = React.useState("");
    const [commercialHierarchyExpanded, setCommercialHierarchyExpanded] = React.useState(false);

    const [showSearchHierarchyBar, setShowSearchHierarchyBar] = React.useState<boolean>(false);

    const searchHierarchyBarRef = React.useRef(null)

    const showHideSearchHierarchyBar = () => {
        setShowSearchHierarchyBar(prevState => {
            if (prevState) {
                clearSearchWord()
            }
            return !prevState
        });
    }

    const commercialHierarchy = hierarchiesTree.find(h => h.hierarchyGuid === commercialHierarchyGUID);

    const setErrorState = (errorType: ErrorType) => {
        setError(errorType)
        setState(DATA_STATE.error);
    }

    const setDefaultVisibility = () => {
        let newSelectedHierarchy: HierarchiesSection = {
            selectedHierarchies: "all_companies",
            selectedCommercialHierarchies: []
        };
        if (!currentUserHierarchies?.empty) {
            if (currentUser.company.name.toLowerCase().includes("cessato")) {
                newSelectedHierarchy = {
                    selectedHierarchies: [hierarchiesTree.find(
                        (h) => h.hierarchyGuid === currentUserHierarchies.hierarchyTypeGuid)?.label, currentUser?.company?.name],
                    selectedCommercialHierarchies: []
                }
                setIsInCeasedCompany(true);
            } else if (currentUserHierarchies.hierarchyTypeGuid === commercialHierarchyGUID) {
                if (commercialHierarchy) {
                    newSelectedHierarchy = {
                        selectedHierarchies: [commercialHierarchyGUID],
                        selectedCommercialHierarchies: CommercialHierarchyTreeToList(
                            currentUserHierarchies.hierarchyNodesGuids,
                            commercialHierarchy)
                    };
                }
            } else {
                newSelectedHierarchy = {
                    selectedHierarchies: HierarchyTreeToList(currentUserHierarchies?.hierarchyNodesGuids[0],
                        hierarchiesTree,
                        currentUserHierarchies?.hierarchyTypeGuid),
                    selectedCommercialHierarchies: []
                };
            }
        }
        setHasFullVisibility(newSelectedHierarchy.selectedHierarchies === "all_companies");
        setHierarchiesSelected(newSelectedHierarchy);
        setCommercialHierarchyExpanded(expandCommercialHierarchy(commercialHierarchy, newSelectedHierarchy, searchWord))
        setState(DATA_STATE.loaded);
    }

    const clearSearchWord = () => setSearchWord("");

    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchWord(e.target.value)
        expandCommercialHierarchy(commercialHierarchy, hierarchiesSelected, searchWord)
    };

    const renderBack = (): JSX.Element => {
        return (
            <div className={"icons-container"}>
                <Display isVisible={!readOnly}>
                    <EditableHierarchy changeEditingStatus={showEditVisibilitySection} isEditingMode={false}/>
                </Display>
                <SearchIcon
                    showHideSearchBar={showHideSearchHierarchyBar}
                    openSearchBarMessage={AppTranslations.filters.filterBy}
                />
                <IconInfoLegend showWarningIcon={isInCeasedCompany}/>
            </div>
        )
    }

    React.useEffect(() => {
        if (searchHierarchyBarRef && showSearchHierarchyBar) {
            searchHierarchyBarRef.current.focus();
        }
    }, [showSearchHierarchyBar])

    React.useEffect(() => {
        switch (currentUserHierarchiesState) {
            case DATA_STATE.loaded: {
                if (hierarchiesTreeStatus === RestDataStatus.LOADED) {
                    setDefaultVisibility();
                }
                break;
            }
            case HierarchyError.INVALID_ROLES_FOR_HIERARCHY:
                setErrorState(ErrorType.INVALID_ROLES_FOR_HIERARCHY);
                break;
            case DATA_STATE.error: {
                setErrorState(ErrorType.CANNOT_RETRIEVE_USER_VISIBILITY_ERROR);
                break;
            }
            case HierarchyError.HIERARCHY_NOT_FOUND:
                // @ts-ignore
                setHierarchiesSelected({});
                setState(DATA_STATE.loaded);
                break;
        }
    }, [currentUserHierarchiesState, currentUserHierarchies, hierarchiesTreeStatus])


    const selectedCommercialHierarchies = getAllSelectedNodes(hierarchiesSelected?.selectedCommercialHierarchies ?? [])

    return <>
        <VeBoxCard className={"Section"}>
            <VeBoxCard.Header
                title={AppTranslations.userCreationProcess.visibilityStageTitle}
                singleLineTitle={true}
                back={state === DATA_STATE.loaded && currentUserHierarchiesState === DATA_STATE.loaded ? renderBack() : undefined}
            />
            <VeBoxCard.Body className={"detail-card"}>
                <Display isVisible={state === DATA_STATE.isLoading || currentUserHierarchiesState === DATA_STATE.isLoading}>
                    <Loader/>
                </Display>
                <Display isVisible={state === DATA_STATE.loaded && currentUserHierarchiesState === DATA_STATE.loaded && isInCeasedCompany}>
                    <VeboxTreeView.ItemsGroup
                        className={"hierarchy-tree"}
                        value={[]}
                    >
                        <VeboxTreeView.Item
                            key={hierarchiesSelected?.selectedHierarchies[0]}
                            expanded={true}
                            label={
                                <>
                                    <div className={"font-weight-400 hierarchy-leaf"}>{hierarchiesSelected?.selectedHierarchies[0]}</div>
                                </>
                            }
                        >
                            <VeboxTreeView.Item
                                key={hierarchiesSelected?.selectedHierarchies[1]}
                                expanded={false}
                                removeInputSpace={true}
                                label={
                                    <>
                                        <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.ceasedCompany}/>
                                        <div className={"font-weight-400 hierarchy-leaf"}>{hierarchiesSelected?.selectedHierarchies[1]}</div>
                                    </>
                                }
                            />
                        </VeboxTreeView.Item>
                    </VeboxTreeView.ItemsGroup>

                </Display>
                <Display isVisible={state === DATA_STATE.loaded && currentUserHierarchiesState === DATA_STATE.loaded && !isInCeasedCompany}>
                    <TextInput
                        elRef={searchHierarchyBarRef}
                        label={AppTranslations.filters.filterBy}
                        styles={{
                            input: {boxSizing: "border-box"},
                            root: {display: (!showSearchHierarchyBar ? "none" : "")}
                        }}
                        className={"margin-bottom-10"}
                        value={searchWord}
                        onChange={changeSearchWord}
                        endInputAddon={
                            <InputAddon position={"end"}>
                                {Boolean(searchWord) && <span
                                    className="material-icons-outlined clickable"
                                    onClick={clearSearchWord}
                                >clear</span>}
                            </InputAddon>
                        }
                    />
                    <div
                        className={"users-management-list-container"}
                    >
                        <VeboxTreeView.ItemsGroup
                            className={"hierarchy-tree"}
                            value={hierarchiesSelected?.selectedHierarchies ?? []}
                        >
                            {hasFullVisibility && (AppTranslations.userCreationProcess.allCompany).toLowerCase()
                                .includes(searchWord.toLowerCase())
                                ? <VeboxTreeView.Item
                                    label={
                                        <>
                                            <Display isVisible={hasFullVisibility}>
                                                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                                            </Display>
                                            <div className={"font-weight-400 hierarchy-leaf"}>
                                                {AppTranslations.userCreationProcess.allCompany}
                                            </div>
                                        </>
                                    }
                                />
                                : undefined
                            }
                            {
                                hierarchiesTree
                                    .filter(h => h.hierarchyGuid !== commercialHierarchyGUID)
                                    .map(hierarchyToFilteredVeboxTreeItem(
                                        searchWord.toLowerCase(),
                                        !currentUserHierarchies?.empty ? currentUserHierarchies.hierarchyNodesGuids[0] : "",
                                        hierarchiesSelected,
                                        hasFullVisibility,
                                        false))
                                    .filter(c => c !== null && c !== undefined)
                            }
                            {
                                commercialHierarchy
                                && ((commercialHierarchy.children ?? []).filter(
                                    hierarchyIncludesPlacedNodes(currentUserHierarchies?.hierarchyNodesGuids ?? [])
                                ).length > 0 || hasFullVisibility)
                                &&
                                (
                                    <VeboxTreeView.Item
                                        key={commercialHierarchy?.hierarchyGuid}
                                        label={
                                            <>
                                                <Display isVisible={hasFullVisibility}>
                                                    <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                                                </Display>
                                                <div className={"font-weight-400 hierarchy-leaf"}>
                                                    {commercialHierarchy?.label}
                                                </div>
                                            </>
                                        }
                                        expanded={commercialHierarchyExpanded}
                                    >
                                        <VeboxTreeView.CheckboxGroup
                                            values={hierarchiesSelected?.selectedCommercialHierarchies ?? []}
                                        >
                                            {(commercialHierarchy?.children ?? []).map(
                                                hierarchyToFilteredVeboxCheckboxTree(
                                                    searchWord,
                                                    currentUserHierarchies?.hierarchyNodesGuids,
                                                    hierarchiesSelected?.selectedCommercialHierarchies,
                                                    selectedCommercialHierarchies, hasFullVisibility, false))}
                                        </VeboxTreeView.CheckboxGroup>
                                    </VeboxTreeView.Item>
                                )
                            }
                        </VeboxTreeView.ItemsGroup>
                    </div>
                </Display>
            </VeBoxCard.Body>
        </VeBoxCard>
    </>

}
const hierarchyToFilteredVeboxTreeItem = (word: string,
                                          placedNodeGuid: string,
                                          hierarchiesSelected: HierarchiesSection,
                                          isFullVisibility: boolean,
                                          hasParentSelected: boolean) => (hierarchy: VeboxModels.HierarchiesTree) => {

    if (!hasParentSelected && !isFullVisibility && !hierarchy.hierarchyGuid.includes(placedNodeGuid)
        && (hierarchy.children ?? []).filter(hierarchyIncludesPlacedNode(placedNodeGuid)).length === 0) {
        return undefined;
    }

    if (!hierarchy.label.toLowerCase().includes(word.toLowerCase()) && (hierarchy.children ?? []).filter(
        hierarchyIncludesWord(word)).length === 0) {
        return undefined;
    }

    const doesIncludeSearchWord = (hierarchy.label.toLowerCase().includes(word.toLowerCase())
            || (hierarchy.children ?? []).filter(hierarchyIncludesWord(word)).length > 0)
        && word !== "";


    const isPlacedHere = placedNodeGuid === hierarchy.hierarchyGuid;

    const label = (
        <>
            <Display isVisible={isPlacedHere}>
                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
            </Display>
            <Display isVisible={isFullVisibility || hasParentSelected}>
                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
            </Display>
            <div className={"font-weight-400 hierarchy-leaf "}>{hierarchy.label}</div>
        </>
    );


    return (
        <VeboxTreeView.Item
            key={hierarchy.hierarchyGuid}
            expanded={!isFullVisibility || doesIncludeSearchWord}
            label={label}
            removeInputSpace={!hasParentSelected}
        >
            {(hierarchy.children ?? [])
                .map(hierarchyToFilteredVeboxTreeItem(
                    word,
                    placedNodeGuid,
                    hierarchiesSelected,
                    isFullVisibility,
                    isPlacedHere || hasParentSelected || isFullVisibility))}
        </VeboxTreeView.Item>
    );
}
const hierarchyToFilteredVeboxCheckboxTree = (word,
                                              placedNodes: string[],
                                              selectedCommercialHierarchies: (string | CommercialHierarchyNode)[],
                                              selectedCommercialHierarchyNodes: string[],
                                              isFullVisibility?: boolean,
                                              hasParentSelected?: boolean) => (hierarchy: VeboxModels.HierarchiesTree) => {

    if (!isFullVisibility
        && !placedNodes.includes(hierarchy.hierarchyGuid)
        && !hasParentSelected
        && (hierarchy.children ?? []).filter(hierarchyIncludesPlacedNodes(placedNodes)).length === 0) {
        return undefined;
    }

    const doesIncludeSearchWord = (hierarchy.label.toLowerCase().includes(word.toLowerCase())
            || (hierarchy.children ?? []).filter(hierarchyIncludesWord(word)).length > 0)
        && word !== "";

    if (!hierarchy.label.toLowerCase().includes(word.toLowerCase())
        && (hierarchy.children ?? []).filter(hierarchyIncludesWord(word)).length === 0) {
        return undefined;
    }


    let shouldBeExpanded: boolean = false;
    let newSelectedCommercialHierarchies: (string | CommercialHierarchyNode)[] = [];

    if (selectedCommercialHierarchies && selectedCommercialHierarchies.length) {
        selectedCommercialHierarchies.forEach((elem) => {
            if ((typeof elem !== "string")) {
                if (!shouldBeExpanded && elem.value === hierarchy.hierarchyGuid
                    && elem.indeterminate
                ) {
                    shouldBeExpanded = true;
                    newSelectedCommercialHierarchies = [...newSelectedCommercialHierarchies, ...elem.children];
                } else {
                    newSelectedCommercialHierarchies = [...newSelectedCommercialHierarchies, elem];
                }
            }
        })
    }

    const isPlacedHere = isPlacedHereInCommercialHierarchy(selectedCommercialHierarchyNodes, hierarchy)

    return (
        <VeboxTreeView.Item
            key={hierarchy.hierarchyGuid}
            expanded={isPlacedHere || doesIncludeSearchWord || shouldBeExpanded}
            label={
                <>
                    <Display isVisible={hasParentSelected || isFullVisibility}>
                        <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                    </Display>
                    <Display isVisible={isPlacedHere}>
                        <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                    </Display>
                    <div className={`font-weight-400 hierarchy-leaf`}>{hierarchy.label}</div>
                </>
            }
            removeInputSpace={!hasParentSelected}
        >
            {(hierarchy.children ?? []).map(
                hierarchyToFilteredVeboxCheckboxTree(word, placedNodes, newSelectedCommercialHierarchies,
                    selectedCommercialHierarchyNodes, isFullVisibility,
                    isPlacedHere || hasParentSelected || isFullVisibility))}
        </VeboxTreeView.Item>
    );
}




